/* Furniture Instore — 5 Year Care Protection Plan, tiered on the real basket.

   Each size in a sofa range is a separate product here, not a variant, so a customer can
   buy a set as separate line items. Pricing the cover from one line's price therefore
   under-prices it: a £2,200 set assembled from an £1,800 "3+2" plus a £400 armchair would
   be covered at the £1,800 rate. The tier has to follow the combined sofa spend, and has
   to keep following it as the basket changes.

   Two jobs, one rule.

   On a product page, the "Complete your order" row previews the tier for
   (what is already in the basket) + (the sofa about to be added), so the price shown before
   adding is the price that will actually apply.

   In the basket, the same sum is enforced: a plan already in the basket is moved to the
   right tier as sofas are added, removed or re-quantified, and dropped when the spend falls
   below the lowest band. A plan is never added for a customer who did not ask for one.

   Which line is a sofa, which is leather and which is the plan is decided in Liquid and
   stamped on each basket row (fi-cart-line-flags) — /cart.js carries no metafields, tags or
   taxonomy category, so script alone cannot tell a sofa from a bed frame. This file only
   ever reads those answers and does the arithmetic.

   Deliberately fail-safe: every entry point returns quietly when the page carries no tier
   payload, no basket rows or no add-on block, so a page this was never meant to touch is
   left exactly as it was. */
(function () {
  'use strict';

  window.FIPDP = window.FIPDP || {};
  if (window.FIPDP.carePlanBooted) return;
  window.FIPDP.carePlanBooted = true;

  /* Trailing debounce on the basket observer. It has to be a trailing edge: one drawer
     re-render fires dozens of mutations and the early ones land while the rows are half
     replaced, so reading the basket on the first of them reads a basket that does not
     exist yet. But a single render's mutations all arrive inside a frame or two, so the
     wait only has to outlast that burst — 250ms was over twice what it needed and sat in
     front of every correction the customer was waiting to see. */
  var RECONCILE_MS = 120;

  /* Long enough to cover fi-cart-drawer-sync's own 450ms debounce, so its scheduled
     drawer-open is still suppressed at the moment it would have fired. */
  var SUPPRESS_MS = 900;

  /* A tier swap is a remove and an add, because /cart/change.js cannot change a line's
     variant — only its quantity. Two writes per swap, and each one re-renders the basket,
     which brings us straight back here. Termination is not left to a timer: every pass
     either finds the plan already correct and stops, or makes one change that moves it
     closer. This cap only exists for the case where the server keeps disagreeing with what
     we asked for — an out-of-stock tier, say — so that a disagreement costs three requests
     rather than an unbounded stream of them. */
  var MAX_WRITES_PER_STATE = 3;

  var payload = null;
  var reconcileTimer = null;
  var busy = false;
  var lastState = '';
  var writesForState = 0;
  var pendingNotice = '';

  /* Its own budget, separate from the tier one. The tier cap resets whenever the basket
     state string changes, which in a runaway it does on every pass — so a cap keyed to
     the state cannot stop a loop that is itself changing the state. This one only resets
     when a pass finds every following line already correct, which is the one condition a
     runaway never reaches. Three writes is more than a settled basket ever needs. */
  var followWrites = 0;
  var MAX_FOLLOW_WRITES = 3;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function money(cents) {
    var fmt = window.FIPDP.moneyFormat || (payload && payload.moneyFormat);
    var value = (cents / 100).toFixed(2);
    if (!fmt) return '£' + value;
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, value)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)));
  }

  /* ---- The price ladder ----
     Rendered by fi-care-plan-data from the tier products' own SKUs. Read once: it is the
     same on every render of the page, and re-parsing it on every basket change would be
     work for nothing. */
  function data() {
    if (payload) return payload;
    var node = document.querySelector('[data-fi-care-plan-data]');
    if (!node) return null;
    try {
      payload = JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
    if (!payload || !payload.tiers || !payload.tiers.length) {
      payload = null;
      return null;
    }
    return payload;
  }

  /* ---- Which tier a spend lands on ----
     The same rule fi-care-plan-pick applies in Liquid, and it has to stay the same rule:
     a preview that disagrees with the basket is worse than no preview.

     Lower bound inclusive, upper bound exclusive, so £500.00 exactly is a 500–1000 sofa.
     A spend above the top band's ceiling clamps to the top band rather than falling out of
     cover — adding a dedicated higher tier to the product list is all it takes to change
     that, because the clamp only ever applies to whichever band is currently the highest.
     Below the lowest band there is no cover to sell, and the answer is none. */
  function pickTier(spend, material) {
    var d = data();
    if (!d) return null;

    var matching = d.tiers.filter(function (tier) {
      return tier.m === material && tier.av;
    });
    if (!matching.length) return null;

    var exact = matching.filter(function (tier) {
      return spend >= tier.lo && spend < tier.hi;
    })[0];
    if (exact) return exact;

    var top = matching.reduce(function (best, tier) {
      return !best || tier.hi > best.hi ? tier : best;
    }, null);
    if (top && spend >= top.hi) return top;

    return null;
  }

  /* ---- What is in the basket ----
     Read off the rendered rows rather than fetched, because the rows carry the Liquid
     answers and /cart.js does not. The drawer and the cart page can both be present; either
     one is a complete picture of the same basket, so the first with rows wins. */
  function basketRows() {
    var containers = [
      document.getElementById('main-cart-items'),
      document.getElementById('CartDrawer-CartItems')
    ];
    for (var i = 0; i < containers.length; i += 1) {
      if (!containers[i]) continue;
      var rows = containers[i].querySelectorAll('[data-fi-key]');
      if (rows.length) return Array.prototype.slice.call(rows);
    }
    /* No rows in a container that exists means an empty basket, which is an answer. */
    return containers.some(Boolean) ? [] : null;
  }

  function readBasket() {
    var rows = basketRows();
    if (!rows) return null;

    var state = { spend: 0, sofaQty: 0, leather: false, plans: [], follows: [] };

    rows.forEach(function (row) {
      /* A following line is never itself a sofa, whatever the flags say. Counted as one
         it would feed its own target: two sofas plus a delivery of two makes four, which
         sets the delivery to four, which makes six, then eight — the runaway that put a
         quantity of twelve against two sofas. The line being written can never be part of
         the number being written. */
      var follows = row.hasAttribute('data-fi-qty-follow');

      /* An add-on is never a sofa, whatever the sofa heuristic decided. Belt and braces
         over the flag itself: "Sofa Removal" has the word sofa in its title and sits in
         the sofa collections, so every text-based test is one blank setting away from
         counting it — and counting it puts the delivery line at two against one sofa. */
      var isAddon = row.hasAttribute('data-fi-addon-line') || row.hasAttribute('data-fi-care-plan');

      if (row.hasAttribute('data-fi-sofa') && !follows && !isAddon) {
        state.spend += parseInt(row.getAttribute('data-fi-spend'), 10) || 0;
        /* A modular build is several lines for one sofa; the line says how many sofas it
           stands for (snippets/fi-cart-line-flags.liquid). */
        var units = row.getAttribute('data-fi-sofa-units');
        state.sofaQty += parseInt(units !== null ? units : row.getAttribute('data-fi-qty'), 10) || 0;
        if (row.hasAttribute('data-fi-leather')) state.leather = true;
      }
      if (follows) {
        state.follows.push({
          key: row.getAttribute('data-fi-key'),
          quantity: parseInt(row.getAttribute('data-fi-qty'), 10) || 1
        });
      }
      if (row.hasAttribute('data-fi-care-plan')) {
        state.plans.push({
          key: row.getAttribute('data-fi-key'),
          variantId: parseInt(row.getAttribute('data-fi-variant'), 10) || 0,
          quantity: parseInt(row.getAttribute('data-fi-qty'), 10) || 1
        });
      }
    });

    return state;
  }

  /* ====
     Product page: preview the tier the basket will actually charge
     ==== */

  function addonBlock() {
    return document.querySelector('[data-fi-addons]');
  }

  function currentSofaLine(block) {
    var price = parseInt(block.getAttribute('data-fi-sofa-price'), 10) || 0;

    /* The stepper is bound to the form by the HTML `form` attribute rather than sitting
       inside it, so a lookup scoped to the form found nothing and every preview was
       priced as a single sofa. Same lookup order as fi-pdp-v5.js. */
    var form = document.querySelector('product-info form[data-type="add-to-cart-form"]');
    var input = document.querySelector('.fi-actions [name="quantity"]');
    if (!input && form) {
      input = form.querySelector('[name="quantity"]');
      if (!input && form.id) input = document.querySelector('[name="quantity"][form="' + form.id + '"]');
    }
    if (!input) input = document.querySelector('product-info [name="quantity"]');

    var qty = input ? parseInt(input.value, 10) : 1;
    if (isNaN(qty) || qty < 1) qty = 1;
    return price * qty;
  }

  function refreshPreview() {
    var block = addonBlock();
    if (!block) return;

    var row = block.querySelector('[data-fi-addon-row="care-plan"]');
    var check = row ? row.querySelector('[data-fi-addon-tier="care-plan"]') : null;
    if (!row || !check) return;

    /* Not a sofa: the block may still be on the page for the other add-ons, but there is no
       sofa spend to tier a plan on. Liquid has already hidden the row; leave it alone. */
    if (!block.hasAttribute('data-fi-is-sofa')) return;

    var basket = readBasket();
    var spend;
    var leather = block.hasAttribute('data-fi-sofa-leather');

    if (basket) {
      spend = basket.spend + currentSofaLine(block);
      if (basket.leather) leather = true;
    } else {
      /* No basket markup on this page at all — fall back to the sum Liquid worked out when
         the block was rendered, which is right until something is added. */
      spend = (parseInt(block.getAttribute('data-fi-basket-sofa'), 10) || 0) + currentSofaLine(block);
    }

    var tier = pickTier(spend, leather ? 'leather' : 'fabric');

    if (!tier) {
      /* Out of range, or the only valid tier is out of stock. Unticked as well as hidden, so
         a plan the customer can no longer see can never reach the basket with the sofa. */
      row.hidden = true;
      check.disabled = true;
      check.checked = false;
      refreshTotal();
      return;
    }

    row.hidden = false;
    check.disabled = false;
    check.value = String(tier.id);
    check.dataset.price = String(tier.p);

    var uid = check.id;
    Array.prototype.forEach.call(
      block.querySelectorAll('[data-fi-addon-price="' + uid + '"]'),
      function (node) {
        node.textContent = money(tier.p);
      }
    );

    var note = row.querySelector('[data-fi-tier-note]');
    if (note) {
      /* The wording is a customiser setting, so Liquid hands it over with [total] still in
         it. Recovering it from the rendered text instead would mean guessing at where the
         money went, in whatever format the shop happens to use. */
      var template = note.getAttribute('data-fi-note-template');
      if (template) note.textContent = template.replace('[total]', money(spend));
    }

    refreshTotal();
  }

  function refreshTotal() {
    if (typeof window.FIPDP.refreshAddonPricing === 'function') window.FIPDP.refreshAddonPricing();
  }

  /* ====
     Basket: keep the plan on the tier the spend calls for
     ==== */

  function showNotice(text) {
    var slots = document.querySelectorAll('[data-fi-care-notice]');
    if (!slots.length) return;
    Array.prototype.forEach.call(slots, function (slot) {
      slot.textContent = text;
      slot.hidden = !text;
    });
  }

  /* fi-cart-drawer-sync opens the drawer after any completed /cart/add it did not see the
     theme make — which is right for every other route into the basket and wrong for this
     one. Moving a tier is not something the customer asked for, so it must not throw the
     drawer open, and on the cart page that sync reloads the page. A counter rather than a
     flag, so two overlapping writes cannot clear each other's suppression early. */
  function suppressDrawer(on) {
    var current = window.FIPDP.suppressDrawerOpen || 0;
    window.FIPDP.suppressDrawerOpen = Math.max(0, current + (on ? 1 : -1));
  }

  /* ---- Re-rendering the basket without opening the drawer ----
     cart-drawer's own renderContents() calls open() at the end of it, which is right when
     the customer has just added something and wrong here: moving a tier is not an action
     they took, and a drawer springing open over the page would be the only sign of it. So
     the sections are replaced directly.

     Both cart components are asked what they need rendered, because a write made on the
     cart page has to bring the page's own line rows back — those rows carry the Liquid
     flags this file reads, and rendering only the drawer's sections would leave it reading
     a stale basket and reconciling against it for ever.

     The theme uses two shapes for a section target: cart-drawer's is
     { id: <section id>, selector }, and cart-items' is { id: <element id>, section, selector }.
     The presence of `section` is what tells them apart. */
  function renderTargets() {
    var out = [];
    var seen = {};

    ['cart-drawer', 'cart-items'].forEach(function (tag) {
      var component = document.querySelector(tag);
      if (!component || typeof component.getSectionsToRender !== 'function') return;

      var list;
      try {
        list = component.getSectionsToRender();
      } catch (e) {
        return;
      }

      list.forEach(function (entry) {
        var sectionId = entry.section || entry.id;
        var node;

        if (entry.section) {
          var host = document.getElementById(entry.id);
          if (!host) return;
          node = (entry.selector && host.querySelector(entry.selector)) || host;
        } else {
          node = entry.selector ? document.querySelector(entry.selector) : document.getElementById(entry.id);
        }
        if (!node) return;

        var mark = sectionId + '>>' + (entry.selector || '');
        if (seen[mark]) return;
        seen[mark] = true;

        out.push({ sectionId: sectionId, node: node, selector: entry.selector });
      });
    });

    return out;
  }

  function post(path, body, withSections) {
    if (withSections) {
      var ids = renderTargets().map(function (target) {
        return target.sectionId;
      });
      if (ids.length) {
        body.sections = ids.filter(function (id, at) {
          return ids.indexOf(id) === at;
        });
        body.sections_url = window.location.pathname;
      }
    }

    return fetch(root() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json();
    });
  }

  function sectionInnerHTML(html, selector) {
    try {
      var found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector || '.shopify-section');
      return found ? found.innerHTML : null;
    } catch (e) {
      return null;
    }
  }

  /* Returns whether the basket on screen now matches the basket on the server. Anything less
     than that is reported as a failure, so the caller falls back rather than leaving the
     rows this file reads out of step with the cart they describe. */
  function refresh(state) {
    if (!state || state.status || !state.sections) return false;

    var targets = renderTargets();
    var applied = 0;

    targets.forEach(function (target) {
      var html = state.sections[target.sectionId];
      if (typeof html !== 'string') return;
      var inner = sectionInnerHTML(html, target.selector);
      if (inner === null) return;
      target.node.innerHTML = inner;
      applied += 1;
    });

    /* The theme hangs "basket is empty" off classes on the wrappers, and they sit outside
       every section that was just replaced. */
    if (applied) {
      var empty = state.item_count === 0;
      ['cart-drawer', 'cart-items', 'cart-drawer-items'].forEach(function (tag) {
        var component = document.querySelector(tag);
        if (component) component.classList.toggle('is-empty', empty);
      });
      var footer = document.getElementById('main-cart-footer');
      if (footer) footer.classList.toggle('is-empty', empty);
    }

    return applied > 0;
  }

  function removeLine(key, withSections) {
    return post('cart/change.js', { id: key, quantity: 0 }, withSections);
  }

  function reconcile() {
    reconcileTimer = null;
    if (busy) return;

    /* Buy Now has already put everything in the basket and is navigating to the checkout.
       A write started now would either be abandoned mid-flight or land after the page has
       gone, and the tier it was correcting is re-read at the checkout anyway. */
    if (window.FIPDP.skipDrawerOpen) return;

    var d = data();
    if (!d) return;

    var basket = readBasket();
    if (!basket) return;

    if (pendingNotice) {
      showNotice(pendingNotice);
      pendingNotice = '';
    }

    /* ---- Add-ons bought once per sofa ----
       The delivery service goes in at the right quantity from the product page, but the
       basket is where people change their mind: putting the sofa up to three in the
       drawer has to take the delivery with it, or three sofas arrive on one delivery
       that was paid for once.

       Ahead of the care-plan work below, and ahead of its "no plan, nothing to do"
       return, because a basket can hold one of these with no plan in it at all.

       Quantity only. Never added, never removed — if the sofas are gone the line is left
       exactly as it is, because a customer who is mid-edit has not asked for it to be
       tidied up, and deleting something they chose is not a correction. */
    if (basket.sofaQty > 0) {
      var offLine = basket.follows.filter(function (line) {
        return line.quantity !== basket.sofaQty && line.key;
      })[0];

      if (!offLine) {
        /* Settled. This is the only place the budget is given back. */
        followWrites = 0;
      } else {
        if (followWrites >= MAX_FOLLOW_WRITES) return;
        followWrites += 1;

        busy = true;
        suppressDrawer(true);

        post('cart/change.js', { id: offLine.key, quantity: basket.sofaQty }, true)
          .then(function (result) {
            /* A rejected write changed nothing, so there is nothing to re-render and
               nothing to reload for — the same rule the tier writes below follow. */
            if (!result || result.status) return;
            if (refresh(result)) return;
            if (window.location.pathname.indexOf('/cart') === 0) window.location.reload();
          })
          .catch(function () {})
          .then(function () {
            busy = false;
            window.setTimeout(function () {
              suppressDrawer(false);
            }, SUPPRESS_MS);
            /* One line per pass: the write re-renders the basket, which brings us back
               here for the next one, if there is a next one. */
            schedule();
          });
        return;
      }
    }

    /* Never auto-add. A basket with no plan in it is a customer who did not want one, and
       that is the end of it. */
    if (!basket.plans.length) return;

    var material = basket.leather ? 'leather' : 'fabric';
    var target = pickTier(basket.spend, material);

    /* One plan per order, tiered on the whole spend — never one plan per sofa.

       Which duplicate survives matters, and it is not simply the first. A swap adds the new
       tier before dropping the old one (see below), so if the drop is the half that fails,
       the basket is left holding both. Keeping "the first" there would keep the wrong tier
       and swap again on the next pass, and again after that. Keeping the one that already
       matches the target ends it in a single pass. */
    var keeper = null;
    if (target) {
      keeper = basket.plans.filter(function (line) {
        return line.variantId === target.id;
      })[0];
    }
    if (!keeper) keeper = basket.plans[0];

    var plan = keeper;
    var extras = basket.plans.filter(function (line) {
      return line !== keeper;
    });

    /* Every write changes this, which is what makes the cap below a backstop against a
       server that keeps disagreeing rather than a limit on ordinary progress. */
    var state = [
      basket.spend,
      material,
      target ? target.id : 'none',
      basket.plans
        .map(function (line) {
          return line.variantId + 'x' + line.quantity;
        })
        .join(',')
    ].join('|');
    if (state !== lastState) {
      lastState = state;
      writesForState = 0;
    }

    var work = null;
    var notice = '';

    if (extras.length) {
      /* One at a time: removing a line re-renders the basket, which brings us back here for
         the next one. Keyed by line key rather than by position, so a shifting line number
         cannot make the second removal hit the wrong row. */
      work = function () {
        return removeLine(extras[0].key, true);
      };
    } else if (!target) {
      notice = d.notice || '';
      work = function () {
        return removeLine(plan.key, true);
      };
    } else if (plan.variantId !== target.id) {
      /* The swap. /cart/change.js can only change a line's quantity, never its variant, so
         moving a tier means two writes. Silent by design: the customer chose cover, and the
         price following their basket is the point of it.

         Add first, remove second, and the order is the whole safety argument. If the add
         fails, the customer keeps the cover they already agreed to at a price they have
         already seen. Removing first and then failing to add would leave them uncovered with
         nothing on screen saying so — the one outcome worth engineering against. */
      work = function () {
        return post('cart/add.js', { items: [{ id: target.id, quantity: 1 }] }, false).then(
          function (added) {
            if (!added || added.status) return added;
            return removeLine(plan.key, true);
          }
        );
      };
    } else if (plan.quantity !== 1) {
      work = function () {
        return post('cart/change.js', { id: plan.key, quantity: 1 }, true);
      };
    }

    if (!work) {
      showNotice('');
      return;
    }

    if (writesForState >= MAX_WRITES_PER_STATE) return;
    writesForState += 1;

    busy = true;
    suppressDrawer(true);

    /* `busy` staying true would disable the whole file for the life of the page, so the call
       is made where a synchronous throw still reaches the handlers that clear it. */
    var running;
    try {
      running = work();
    } catch (e) {
      running = Promise.reject(e);
    }

    running
      .then(function (result) {
        /* A rejected write changed nothing: the rows on screen still describe the basket
           behind them, there is no removal to announce, and there is certainly nothing to
           reload the page for. Saying otherwise here is how three failed attempts would
           have become three page reloads. */
        if (!result || result.status) return;

        /* The notice has to outlive the render that is about to wipe it, so it is held and
           written by the pass the render brings us back on. */
        if (notice) pendingNotice = notice;
        if (refresh(result)) return;

        /* Nothing rendered, so the rows on screen no longer describe the basket behind them.
           On the cart page that is visible and worth a reload; anywhere else the drawer is
           shut, and the next thing to open it will render it correctly. */
        if (window.location.pathname.indexOf('/cart') === 0) window.location.reload();
      })
      .catch(function () {
        /* The basket is whatever the server says it is; a failed tier move leaves the
           customer with valid cover at a price they already agreed to, which is a far better
           outcome than a half-applied change. */
      })
      .then(function () {
        busy = false;
        window.setTimeout(function () {
          suppressDrawer(false);
        }, SUPPRESS_MS);
        schedule();
      });
  }

  function schedule() {
    window.clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(reconcile, RECONCILE_MS);
  }

  /* ---- What tells us the basket moved ----
     The theme announces its own cart writes, but app scripts and this file's own writes
     arrive by other routes, so the rendered rows are watched as well. Both funnel into one
     debounced pass, so a re-render's burst of mutations costs one reconcile. */
  function watch() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        schedule();
        refreshPreview();
      });
    } else {
      window.setTimeout(watch, 300);
      return;
    }

    if (!('MutationObserver' in window)) return;

    /* The custom elements themselves are never replaced — only their contents are, whether
       by the theme, by this file or by an app. So one observer per element survives every
       re-render, and watching two small subtrees costs a fraction of watching the body.

       cart-drawer rather than #CartDrawer: the drawer's own render replaces #CartDrawer
       outright, and an observer on a node that gets replaced stops hearing anything. */
    var observer = new MutationObserver(function () {
      schedule();
      refreshPreview();
    });

    ['cart-drawer', 'cart-items'].forEach(function (tag) {
      var component = document.querySelector(tag);
      if (component) observer.observe(component, { childList: true, subtree: true });
    });
  }

  /* The quantity stepper moves the sofa line the preview is priced on, and it is inside the
     buy column that product-info.js re-renders, so it is listened for on document. */
  document.addEventListener('change', function (event) {
    if (event.target && event.target.matches && event.target.matches('[name="quantity"]')) {
      refreshPreview();
    }
  });

  /* Called by fi-pdp-v5.js from its own re-apply pass, so a variant change recomputes the
     tier without a second observer on the buy column. */
  window.FIPDP.refreshCarePlanPreview = refreshPreview;

  function init() {
    if (!data()) return;
    refreshPreview();
    schedule();
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
